import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Button, Card } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { TagDto, TagUpsertDto } from '@/common/models/gamification'
import { createTag, deleteTag, getTags, updateTag } from '@/core/services/gamification'

import { MessageBox } from '../messages/MessageBox'
import { CreateTagModal } from './CreateTagModal'
import { TagChip } from './shared/TagChip'

export const TagsPage: React.FC = () => {
  const { editionId } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [tags, setTags] = useState<TagDto[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<TagDto | null>(null)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }

  const notify = (msg: string) => {
    setIsError(false)
    setMessage(msg)
    setShowMessageBox(true)
  }

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      setLoading(true)
      const { data } = await getTags(editionId)
      setTags(data ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [editionId])

  useEffect(() => {
    getData()
  }, [getData])

  const openCreate = () => {
    setEditing(null)
    setCreateOpen(true)
  }

  const openEdit = (row: TagDto) => {
    if (!hasEditPermission) return
    setEditing(row)
    setCreateOpen(true)
  }

  const handleSubmit = async (values: TagUpsertDto, id?: string) => {
    if (!editionId) return
    try {
      setLoading(true)
      if (id) {
        await updateTag(id, values)
        notify('Tag updated successfully')
      } else {
        await createTag(editionId, values)
        notify('Tag created successfully')
      }
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setLoading(true)
      await deleteTag(id)
      notify('Tag deleted successfully')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Tag',
        accessor: 'translations',
        render: (row: TagDto) => <TagChip tag={row} />,
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row: TagDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEdit(row)
            }}
          >
            Edit
          </a>
        ),
      },
    ],
    [hasEditPermission],
  )

  return (
    <>
      <Header
        pageTitle="Gamification"
        actions={
          hasEditPermission && (
            <Button variant="primary-bordered" onClick={openCreate}>
              Add Tag
              <Icon icon="plus-circle" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle icon="tag" title="Tags" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={tags}
          noDataMessage="No tags yet"
        />
      </Card>

      <CreateTagModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        tag={editing}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
